/**
 * Screen 11 — client companies (super-admin only).
 *
 * Creating a company provisions its first `ORG_ADMIN` in the same call — the
 * "main profile" from which the company then creates its own sub-users, which is
 * the user-management model the client asked for on 2026-07-30. Nobody sets
 * anyone else's password: the administrator is invited and chooses their own.
 *
 * The retention and direct-download settings are here because they are per
 * organisation, but they are bounded by law, not by preference: the contract
 * caps direct download at 7–30 days (contracted 14) and floors retention at the
 * BOE Second.4 minimum of 12 months (defaulted to 24).
 */

import { useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { DecaApiError, unwrap, type Organisation, type OrganisationSettings } from '@deca/api'

import { api } from '../app/api.ts'
import {
  apiErrorMessage,
  autocompletePeriodRefusal,
  invalidEmailMessage,
  undeliverableEmailMessage,
} from '../app/errors.ts'
import { qk, useOrganisations } from '../app/queries.ts'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../app/auth.tsx'
import { abilitiesFor } from '../deca/roles.ts'
import { emailAddressRules, isEmailAddress } from '../deca/emailAddress.ts'
import { formatDate } from '../deca/format.ts'
import { AdminShell } from './AdminShell.tsx'
import { Button, Card, Chip, NoAccessState, Notice } from '../ui/primitives.tsx'
import { DataTable, Pagination, type Column } from '../ui/DataTable.tsx'
import { Dialog } from '../ui/Dialog.tsx'
import { SelectInput, TextInput } from '../ui/form.tsx'
import { useToast } from '../ui/Toast.tsx'
import { useStepBackPastEnd } from '../ui/useStepBackPastEnd.ts'
import { SettingBox } from '../ui/SettingBox.tsx'
import { AutocompleteHoursSelect, DEFAULT_AUTOCOMPLETE_HOURS, type AutocompleteHours } from './AutocompleteHoursSelect.tsx'
import { PAGE_DEFAULT, pagedSearchSchema, PER_PAGE } from './searchSchemas.ts'

interface OrgFormValues {
  legalName: string
  nif: string
  address: string
  adminEmail: string
  adminName: string
  directDownloadDays: number
  retentionMonths: number
  transportType: TransportType
}

/** «Tipo de transporte» (#220): the three values, in the order offered. */
type TransportType = NonNullable<OrganisationSettings['transportType']>
const TRANSPORT_TYPES: readonly TransportType[] = ['GOODS', 'PASSENGER', 'BOTH']

/**
 * The company's «Tipo de transporte» (#220): which document type the portal and ASG Mobile offer it.
 * The platform's alone, so it is in «Crear empresa» and «Editar empresa» and nowhere a company's own
 * administrator edits.
 */
function TransportTypeSelect(props: React.ComponentProps<'select'>) {
  const { t } = useTranslation()
  return (
    <SelectInput
      label={t('admin.organisations.transportType')}
      help={t('admin.organisations.transportTypeHelp')}
      {...props}
    >
      {TRANSPORT_TYPES.map((type) => (
        <option key={type} value={type}>
          {t(`admin.organisations.transportTypes.${type}`)}
        </option>
      ))}
    </SelectInput>
  )
}

/**
 * The bounds, from the contract the whole portal is generated from — `directDownloadDays`
 * 7–30 (BOE Third.5) and `retentionMonths` at least 12 (BOE Second.4).
 */
const DIRECT_DOWNLOAD_DAYS = { min: 7, max: 30 }
const RETENTION_MONTHS = { min: 12 }

/**
 * Register options that actually reject an out-of-range number.
 *
 * The `min`/`max` **attributes** on a number input are HTML constraint validation, and
 * nothing in these dialogs triggers it: `Dialog` renders no `<form>`, `Button` is always
 * `type="button"`, and Save calls `handleSubmit` programmatically — so the browser never
 * runs its own check and only react-hook-form's rules decide. These are those rules.
 *
 * `required` is what covers a *cleared* field: `valueAsNumber` turns an empty input into
 * `NaN`, `NaN < 7` is false so the `min` rule lets it through, and `JSON.stringify` writes
 * it as `null` — a 400 from a field the person emptied rather than mistyped.
 */
function boundedNumber(t: TFunction, bounds: { min: number; max?: number }) {
  return {
    valueAsNumber: true,
    required: t('errors.required'),
    min: { value: bounds.min, message: t('errors.min', { min: bounds.min }) },
    ...(bounds.max === undefined
      ? {}
      : { max: { value: bounds.max, message: t('errors.max', { max: bounds.max }) } }),
  }
}

export function AdminOrganisationsScreen() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const client = useQueryClient()
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)

  const search = pagedSearchSchema.parse(useSearch({ strict: false }))
  const list = useOrganisations(
    { page: search.page ?? PAGE_DEFAULT, perPage: PER_PAGE, ...(search.q ? { q: search.q } : {}) },
    abilities.platformAdmin,
  )
  // A page past the end shows the last page, not «Sin resultados» (#165).
  useStepBackPastEnd(list.data?.meta, search.page ?? PAGE_DEFAULT, (page) => {
    void navigate({ to: '/admin/empresas', search: { ...search, page }, replace: true })
  })

  const [createOpen, setCreateOpen] = useState(false)
  const [editingOrg, setEditingOrg] = useState<Organisation | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (!abilities.platformAdmin) {
    return (
      <AdminShell>
        <Card>
          <NoAccessState />
        </Card>
      </AdminShell>
    )
  }

  async function act(action: () => Promise<unknown>, message: string) {
    setError(null)
    try {
      await action()
      void client.invalidateQueries({ queryKey: qk.organisations() })
      toast.success(message)
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
    }
  }

  const columns: Column<Organisation>[] = [
    {
      key: 'legalName',
      header: t('admin.organisations.legalName'),
      render: (row) => (
        <div className="stack-sm" style={{ gap: 0 }}>
          <span style={{ fontWeight: 600 }}>{row.legalName}</span>
          <span className="small muted tabular">{row.nif}</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('admin.organisations.status'),
      // At the end of a phone card's heading (#111), beside the company it is about.
      card: 'end',
      render: (row) => (
        <Chip
          tone={row.status === 'ACTIVE' ? 'success' : 'warning'}
          icon={row.status === 'ACTIVE' ? 'checkCircle' : 'ban'}
        >
          {t(`admin.organisations.status${row.status}`)}
        </Chip>
      ),
    },
    {
      key: 'users',
      header: t('admin.organisations.users'),
      align: 'right',
      numeric: true,
      render: (row) => row.userCount ?? 0,
    },
    {
      key: 'decas',
      header: t('admin.organisations.decas'),
      align: 'right',
      numeric: true,
      render: (row) => row.decaCount ?? 0,
    },
    {
      key: 'settings',
      header: t('admin.organisations.settings'),
      render: (row) => (
        <span className="small muted tabular">
          {row.settings?.directDownloadDays ?? 14} d · {row.settings?.retentionMonths ?? 24} m
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: t('common.createdAt'),
      numeric: true,
      render: (row) => formatDate(row.createdAt, i18n.language),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'right',
      render: (row) => (
        <div className="table__actions">
          <Button size="sm" variant="ghost" icon="edit" onClick={() => setEditingOrg(row)}>
            {t('admin.organisations.edit')}
          </Button>
          {row.status === 'ACTIVE' ? (
            <Button
              size="sm"
              variant="ghost"
              icon="ban"
              onClick={() => {
                // A separate confirmation, not a switch inside the edit form: `SUSPENDED`
                // denies sign-in to every user of this company. /auth/login refuses the
                // session whenever the organisation is not ACTIVE — after the password,
                // and answering the same "credenciales incorrectas" as a wrong one, so a
                // suspended user is told nothing and has nothing to correct.
                if (!window.confirm(t('admin.organisations.suspendConfirm'))) return
                void act(
                  () =>
                    unwrap(
                      api.PATCH('/api/v1/organisations/{orgId}', {
                        params: { path: { orgId: row.id } },
                        body: { status: 'SUSPENDED' },
                      }),
                    ),
                  t('admin.organisations.suspended'),
                )
              }}
            >
              {t('admin.organisations.suspend')}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              icon="checkCircle"
              onClick={() =>
                // Restoring access is not the destructive direction, so it needs no confirmation.
                void act(
                  () =>
                    unwrap(
                      api.PATCH('/api/v1/organisations/{orgId}', {
                        params: { path: { orgId: row.id } },
                        body: { status: 'ACTIVE' },
                      }),
                    ),
                  t('admin.organisations.reactivated'),
                )
              }
            >
              {t('admin.organisations.reactivate')}
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <AdminShell heading={t('admin.organisations.title')} subtitle={t('admin.organisations.subtitle')}>
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <Card
        flush
        actions={
          <Button variant="primary" icon="plus" onClick={() => setCreateOpen(true)}>
            {t('admin.organisations.create')}
          </Button>
        }
      >
        <DataTable
          rows={list.data?.data}
          columns={columns}
          rowKey={(row) => row.id}
          caption={t('admin.organisations.title')}
          isLoading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
        />
        {list.data?.meta ? (
          <Pagination
            page={list.data.meta.page}
            perPage={list.data.meta.perPage}
            total={list.data.meta.total}
            totalPages={list.data.meta.totalPages}
            onPage={(page) => void navigate({ to: '/admin/empresas', search: { ...search, page } })}
          />
        ) : null}
      </Card>

      <CreateOrganisationDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          void client.invalidateQueries({ queryKey: qk.organisations() })
          toast.success(t('admin.organisations.created'))
        }}
      />

      {editingOrg ? (
        // `key` remounts the dialog whenever a different row is opened, so react-hook-form's
        // `defaultValues` — read once at mount — are always that row's own values rather than
        // whichever organisation was edited previously.
        <EditOrganisationDialog
          key={editingOrg.id}
          organisation={editingOrg}
          onClose={() => setEditingOrg(null)}
          onUpdated={() => {
            void client.invalidateQueries({ queryKey: qk.organisations() })
            toast.success(t('admin.organisations.updated'))
          }}
        />
      ) : null}
    </AdminShell>
  )
}

/** Exported for `src/test/address-dialogs.test.tsx`. */
export function CreateOrganisationDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: () => void
}) {
  const { t } = useTranslation()
  const form = useForm<OrgFormValues>({
    defaultValues: {
      legalName: '',
      nif: '',
      address: '',
      adminEmail: '',
      adminName: '',
      directDownloadDays: 14,
      retentionMonths: 24,
      // The column's default (#220).
      transportType: 'GOODS',
    },
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(values: OrgFormValues) {
    setBusy(true)
    setError(null)
    try {
      await unwrap(
        api.POST('/api/v1/organisations', {
          body: {
            legalName: values.legalName,
            nif: values.nif,
            address: values.address,
            settings: {
              directDownloadDays: Number(values.directDownloadDays),
              retentionMonths: Number(values.retentionMonths),
              locale: 'es',
              transportType: values.transportType,
            },
            admin: { email: values.adminEmail, fullName: values.adminName, locale: 'es' },
          },
        }),
      )
      form.reset()
      onCreated()
      onClose()
    } catch (caught) {
      // The admin's address is checked before the company is created, so a domain that takes no
      // mail is refused with nothing to tidy up, and said on the field that has to change — as is
      // an address the route schema refuses, which it reports under `admin`, where the body has it.
      const onAddress =
        undeliverableEmailMessage(caught, t) ?? invalidEmailMessage(caught, t, 'body/admin/email')
      if (onAddress) form.setError('adminEmail', { message: onAddress }, { shouldFocus: true })
      else setError(apiErrorMessage(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      busy={busy}
      title={t('admin.organisations.create')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={busy}
            onClick={() => void form.handleSubmit(submit)()}
          >
            {t('common.create')}
          </Button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <div className="form-grid form-grid--full">
        <TextInput
          label={t('admin.organisations.legalName')}
          required
          error={form.formState.errors.legalName?.message}
          {...form.register('legalName', { required: t('errors.required') })}
        />
        <TextInput
          label={t('admin.organisations.nif')}
          required
          error={form.formState.errors.nif?.message}
          {...form.register('nif', { required: t('errors.required') })}
        />
        <TextInput label={t('admin.organisations.address')} full {...form.register('address')} />
      </div>

      <div className="stack-sm">
        <h3>{t('admin.organisations.adminProfile')}</h3>
        <p className="small muted">{t('admin.organisations.adminHelp')}</p>
        <div className="form-grid">
          <TextInput
            label={t('admin.organisations.adminName')}
            required
            error={form.formState.errors.adminName?.message}
            {...form.register('adminName', { required: t('errors.required') })}
          />
          <TextInput
            label={t('admin.organisations.adminEmail')}
            type="email"
            required
            error={form.formState.errors.adminEmail?.message}
            {...form.register('adminEmail', emailAddressRules(t))}
          />
        </div>
      </div>

      <div className="stack-sm">
        <h3>{t('admin.organisations.settings')}</h3>
        <div className="form-grid">
          <TextInput
            label={t('admin.organisations.directDownloadDays')}
            help={t('admin.organisations.directDownloadDaysHelp')}
            type="number"
            min={DIRECT_DOWNLOAD_DAYS.min}
            max={DIRECT_DOWNLOAD_DAYS.max}
            error={form.formState.errors.directDownloadDays?.message}
            {...form.register('directDownloadDays', boundedNumber(t, DIRECT_DOWNLOAD_DAYS))}
          />
          <TextInput
            label={t('admin.organisations.retentionMonths')}
            help={t('admin.organisations.retentionMonthsHelp')}
            type="number"
            min={RETENTION_MONTHS.min}
            error={form.formState.errors.retentionMonths?.message}
            {...form.register('retentionMonths', boundedNumber(t, RETENTION_MONTHS))}
          />
          <TransportTypeSelect {...form.register('transportType')} />
        </div>
      </div>
    </Dialog>
  )
}

interface EditOrgFormValues {
  legalName: string
  nif: string
  address: string
  directDownloadDays: number
  retentionMonths: number
  locale: 'es' | 'en'
  driversMayCreate: boolean
  driversMayModify: boolean
  autocompleteHours: AutocompleteHours
  /** «Correo para respuestas de los envíos de DeCA» (#219), as typed; empty is none. */
  decaEmailReplyTo: string
  transportType: TransportType
}

/** #110's two driver permissions, worded once for both screens that set them. */
const DRIVER_SETTINGS = ['driversMayCreate', 'driversMayModify'] as const

/**
 * Screen 11's edit half — the create dialog's own shape, minus the admin profile
 * (that is a one-time provisioning step, not a company detail).
 *
 * **The NIF can be corrected here since 2026-10-13.** It was withheld until then, because a
 * company's NIF is printed on the documents it issued as that party, and changing it reads as
 * a different company. But ASGTRANS creates test companies under placeholders («APIPRUEBA39»),
 * one of them became a real customer's, and nobody at ASGTRANS could put its CIF right: twelve
 * such companies stood in production, and the correction had to be asked of us. The contract
 * always accepted the field on this operation. So the form offers it, says under it what the
 * change does not do (documents already issued keep what they were issued with), and sends it
 * only when it was changed. This dialog is opened from the platform's list alone, so it is the
 * «Administrador ASGTRANS» who corrects it; a company's own «Empresa» tab does not carry it.
 *
 * `status` is not here either — suspension goes through the row's own
 * confirmation, not a switch buried in a save button (see `AdminOrganisationsScreen`).
 *
 * The two numeric bounds (`directDownloadDays` 7–30, `retentionMonths` ≥ 12) are
 * the same rule `admin.ts`'s route schema enforces, taken from the same contract
 * every type in this portal is generated from — not a second copy of it, and it
 * stops a person meeting a 400 this form could have prevented. They are
 * react-hook-form rules (`boundedNumber`), because the HTML attributes alone
 * never run here. The server stays the authority: it re-checks both, and
 * restricts them and `status` to a platform admin regardless of what this form
 * sends.
 *
 * **The PATCH carries only what changed.** `direct_download_days` is nullable and
 * NULL means "follow the service default", which the serialiser resolves before
 * the portal ever sees it (`admin.ts`'s `toOrganisationBody`). Writing every field
 * back would turn that resolved number into an explicit column value, so a company
 * tracking the service default would silently stop tracking it the first time
 * somebody corrected its address. `dirtyFields` is what keeps an edit an edit.
 *
 * Exported for `src/test/edit-organisation-dialog.test.tsx`. The comment on the
 * `dirty` read below is advice; that test is the guard.
 */
export function EditOrganisationDialog({
  organisation,
  onClose,
  onUpdated,
}: {
  organisation: Organisation
  onClose: () => void
  onUpdated: () => void
}) {
  const { t } = useTranslation()
  const form = useForm<EditOrgFormValues>({
    // No `?? 14` / `?? 24` here: `toOrganisationBody` always emits all three settings —
    // it resolves a null `direct_download_days` against the service config, and the other
    // two columns are NOT NULL. A fallback would only be a second, drifting copy of a
    // default that lives in the server's configuration.
    defaultValues: {
      legalName: organisation.legalName,
      nif: organisation.nif,
      address: organisation.address ?? '',
      directDownloadDays: organisation.settings?.directDownloadDays,
      retentionMonths: organisation.settings?.retentionMonths,
      locale: organisation.settings?.locale ?? 'es',
      // The server always sends both; `false` is the column default, not a second copy of a
      // configured one.
      driversMayCreate: organisation.settings?.driversMayCreate ?? false,
      driversMayModify: organisation.settings?.driversMayModify ?? false,
      // The column's default (#180), as `false` is the permissions' column default above.
      autocompleteHours: organisation.settings?.autocompleteHours ?? DEFAULT_AUTOCOMPLETE_HOURS,
      decaEmailReplyTo: organisation.settings?.decaEmailReplyTo ?? '',
      // The column's default (#220), for a server before it.
      transportType: organisation.settings?.transportType ?? 'GOODS',
    },
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Read here, in render, on purpose. `formState` is a Proxy and react-hook-form only keeps
  // up to date — and only re-renders on — the slices something actually read while rendering.
  // Reading `dirtyFields` only inside `submit` leaves it permanently `{}`, which would make
  // every save a silent no-op rather than a PATCH. Moving this line is what
  // `src/test/edit-organisation-dialog.test.tsx` fails on.
  const dirty = form.formState.dirtyFields

  async function submit(values: EditOrgFormValues) {
    const settings: Partial<OrganisationSettings> = {}
    if (dirty.directDownloadDays) settings.directDownloadDays = values.directDownloadDays
    if (dirty.retentionMonths) settings.retentionMonths = values.retentionMonths
    if (dirty.locale) settings.locale = values.locale
    // Dirty only, like the rest: an address fix by ASGTRANS must not re-send a permission the
    // company's own administrator has changed in *Empresa* since this dialog was opened.
    if (dirty.driversMayCreate) settings.driversMayCreate = values.driversMayCreate
    if (dirty.driversMayModify) settings.driversMayModify = values.driversMayModify
    // Dirty only (#180): a fix by ASGTRANS must not re-send a period the company has changed since.
    if (dirty.autocompleteHours) settings.autocompleteHours = values.autocompleteHours
    // Dirty only (#219), and emptied is cleared: `null`, which the server stores as none.
    if (dirty.decaEmailReplyTo) settings.decaEmailReplyTo = values.decaEmailReplyTo.trim() || null
    // Dirty only (#220), as every setting here.
    if (dirty.transportType) settings.transportType = values.transportType

    const body: { legalName?: string; nif?: string; address?: string; settings?: OrganisationSettings } = {
      ...(dirty.legalName ? { legalName: values.legalName } : {}),
      // As the server stores it (`admin.ts` trims and upper-cases), so what is sent is what is kept.
      ...(dirty.nif ? { nif: values.nif.trim().toUpperCase() } : {}),
      // Cleared means `''`, not `null`: neither the route schema nor the contract's own
      // request body accepts a null here, only the `Organisation` it answers with.
      ...(dirty.address ? { address: values.address } : {}),
      // The contract lists none of the three settings as required and `admin.ts` writes only
      // the keys it is given (`settingsColumns`), so a partial is what a PATCH should send.
      // `OrganisationSettings` is generated with all three required only because
      // openapi-typescript promotes any property that declares a `default`; the assertion is
      // that generation artefact, not a claim about the request.
      ...(Object.keys(settings).length > 0 ? { settings: settings as OrganisationSettings } : {}),
    }

    if (Object.keys(body).length === 0) {
      onClose()
      return
    }

    setBusy(true)
    setError(null)
    try {
      await unwrap(
        api.PATCH('/api/v1/organisations/{orgId}', {
          params: { path: { orgId: organisation.id } },
          body,
        }),
      )
      onUpdated()
      onClose()
    } catch (caught) {
      // Another company holds that NIF: said on the field it is about, in the catalogue's words.
      if (caught instanceof DecaApiError && caught.code === 'ORGANISATION_NIF_TAKEN') {
        form.setError('nif', { message: t('admin.organisations.nifExists') })
        return
      }
      // The reply address is the one address this dialog sends (#219): a domain that takes no mail (#141) is
      // said on its field, naming the domain.
      const onReplyTo = undeliverableEmailMessage(caught, t)
      if (onReplyTo) {
        form.setError('decaEmailReplyTo', { message: onReplyTo }, { shouldFocus: true })
        return
      }
      // A shorter window can refuse the company's period (#180): the sentence says what it allows.
      setError(autocompletePeriodRefusal(caught, t) ?? apiErrorMessage(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      busy={busy}
      title={t('admin.organisations.editTitle')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={busy}
            onClick={() => void form.handleSubmit(submit)()}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <div className="form-grid form-grid--full">
        <TextInput
          label={t('admin.organisations.legalName')}
          required
          error={form.formState.errors.legalName?.message}
          {...form.register('legalName', { required: t('errors.required') })}
        />
        <TextInput
          label={t('admin.organisations.nif')}
          required
          maxLength={20}
          help={t('admin.organisations.nifEditHelp')}
          error={form.formState.errors.nif?.message}
          {...form.register('nif', {
            // Whitespace alone is not a NIF: the server would trim it to nothing and refuse.
            validate: (value) => value.trim() !== '' || t('errors.required'),
          })}
        />
        <TextInput label={t('admin.organisations.address')} full {...form.register('address')} />
      </div>

      <div className="stack-sm">
        <h3>{t('admin.organisations.settings')}</h3>
        <div className="form-grid">
          <TextInput
            label={t('admin.organisations.directDownloadDays')}
            help={t('admin.organisations.directDownloadDaysHelp')}
            type="number"
            min={DIRECT_DOWNLOAD_DAYS.min}
            max={DIRECT_DOWNLOAD_DAYS.max}
            error={form.formState.errors.directDownloadDays?.message}
            {...form.register('directDownloadDays', boundedNumber(t, DIRECT_DOWNLOAD_DAYS))}
          />
          <TextInput
            label={t('admin.organisations.retentionMonths')}
            help={t('admin.organisations.retentionMonthsHelp')}
            type="number"
            min={RETENTION_MONTHS.min}
            error={form.formState.errors.retentionMonths?.message}
            {...form.register('retentionMonths', boundedNumber(t, RETENTION_MONTHS))}
          />
          <SelectInput label={t('admin.organisations.locale')} help={t('admin.organisations.localeHelp')} {...form.register('locale')}>
            <option value="es">Español</option>
            <option value="en">English</option>
          </SelectInput>
          <AutocompleteHoursSelect
            value={form.watch('autocompleteHours')}
            disabled={busy}
            onChange={(hours) => form.setValue('autocompleteHours', hours, { shouldDirty: true })}
          />
          <TransportTypeSelect disabled={busy} {...form.register('transportType')} />
          <TextInput
            label={t('admin.company.replyTo')}
            optional
            type="email"
            maxLength={254}
            help={t('admin.company.replyToHelp')}
            error={form.formState.errors.decaEmailReplyTo?.message}
            {...form.register('decaEmailReplyTo', {
              validate: (value) => value.trim() === '' || isEmailAddress(value.trim()) || t('errors.invalidEmail'),
            })}
          />
        </div>
        <div className="stack-sm">
          {DRIVER_SETTINGS.map((key) => (
            <SettingBox
              key={key}
              label={t(`admin.company.${key}`)}
              help={t(`admin.company.${key}Help`)}
              checked={form.watch(key)}
              disabled={busy}
              onChange={(checked) => form.setValue(key, checked, { shouldDirty: true })}
            />
          ))}
        </div>
      </div>
    </Dialog>
  )
}
